import { observer } from 'mobx-react-lite'
import { type ChangeEvent, useCallback } from 'react'
import { bindInput } from '@/infra/view-binding'
import type { FilterProps } from '@/infra/type-utils'
import { TextInput, type TextInputProps } from '@/components/inputs/TextInput'
import {
  type ContainerProps,
  FormFieldContainer,
} from '@/components/FormContainer'
import { useInputMask } from '@/hooks/input-mask'
import { type MaskBlock } from '@/utils/input-mask-util'
import { isNotNil } from '@/utils/object-util'

interface TextInputContainerControlProps extends Omit<
  TextInputProps,
  'value' | 'onChange'
> {
  mask?: MaskBlock[]
}

/**
 * Form field container for a {@link TextInput}.
 */
export const TextInputContainer = observer(function TextInputContainer<
  M extends object,
  K extends keyof FilterProps<M, string>,
>({
  name,
  model,
  label,
  sublabel,
  help,
  field,
  error,
  mask,
  ...rest
}: ContainerProps<M, string, K, TextInputContainerControlProps>) {
  const { value, onChange: setValue } = bindInput(model, field)
  const { maskedValue, onChange: maskedOnChange } = useInputMask({
    mask,
    value: isNotNil(value) ? value : '',
    onChange: setValue,
  })
  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => maskedOnChange(e.target.value),
    [maskedOnChange],
  )
  return (
    <FormFieldContainer
      name={name}
      model={model}
      label={label}
      sublabel={sublabel}
      help={help}
      field={field}
      error={error}
      render={(controlProps) => (
        <TextInput
          value={maskedValue}
          onChange={onChange}
          status={controlProps.invalid ? 'danger' : 'default'}
          name={controlProps.name}
          {...rest}
        />
      )}
    />
  )
})
